import { Component, createRef } from "react";
import { Accordion, Alert, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ArrowBack from "../../assets/icons/arrow_back.svg";
import Check from "../../assets/icons/check.svg";
import Close from "../../assets/icons/close.svg";
import GetApp from "../../assets/icons/get_app.svg";
import Publish from "../../assets/icons/publish.svg";
import CenterBox from "../../components/CenterBox";
import IconButton from "../../components/IconButton";
import {
  DEFAULT_THEME,
  applyThemeVars,
  exportThemeJson,
  importThemeJson,
  resetTheme,
  saveTheme
} from "../../branding/theme";

// CSS defaults for preview when theme values are null
const CSS_DEFAULTS = {
  surface: "#ffffff",
  surfaceDark: "#343a40",
  text: "#111827",
  textMuted: "#6c757d",
  textInverse: "#ffffff",
  success: "#7bd447",
  error: "#dc3545",
  borderColor: "rgba(0, 0, 0, 0.15)",
  overlayBg: "rgba(0, 0, 0, 0.75)"
};

class ThemeEditor extends Component {
  constructor(props) {
    super(props);

    const currentTheme = window.__EXPO_THEME__ || DEFAULT_THEME;
    this.savedTheme = { ...currentTheme };

    this.state = {
      // Basic
      primaryColor: currentTheme.primaryColor,
      secondaryColor: currentTheme.secondaryColor,
      bgStart: currentTheme.bgStart,
      bgEnd: currentTheme.bgEnd,
      logo: currentTheme.logo || null,
      fontFamily: currentTheme.fontFamily || "",

      // Advanced
      surface: currentTheme.surface || "",
      surfaceDark: currentTheme.surfaceDark || "",
      surfaceMuted: currentTheme.surfaceMuted || "",
      text: currentTheme.text || "",
      textMuted: currentTheme.textMuted || "",
      textInverse: currentTheme.textInverse || "",
      success: currentTheme.success || "",
      error: currentTheme.error || "",
      borderColor: currentTheme.borderColor || "",
      overlayBg: currentTheme.overlayBg || "",

      // UI state
      successMessage: null,
      errorMessage: null,
      showAdvanced: false
    };

    this.importFileRef = createRef();

    this.handleChange = this.handleChange.bind(this);
    this.handleLogoChange = this.handleLogoChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleExport = this.handleExport.bind(this);
    this.handleImportClick = this.handleImportClick.bind(this);
    this.handleImportFile = this.handleImportFile.bind(this);
    this.applyLivePreview = this.applyLivePreview.bind(this);
  }

  componentWillUnmount() {
    // Restore saved theme when leaving without saving
    applyThemeVars(this.savedTheme);
  }

  getThemeFromState() {
    return {
      logo: this.state.logo,
      primaryColor: this.state.primaryColor,
      secondaryColor: this.state.secondaryColor,
      fontFamily: this.state.fontFamily || null,
      bgStart: this.state.bgStart,
      bgEnd: this.state.bgEnd,
      surface: this.state.surface || null,
      surfaceDark: this.state.surfaceDark || null,
      surfaceMuted: this.state.surfaceMuted || null,
      text: this.state.text || null,
      textMuted: this.state.textMuted || null,
      textInverse: this.state.textInverse || null,
      success: this.state.success || null,
      error: this.state.error || null,
      borderColor: this.state.borderColor || null,
      overlayBg: this.state.overlayBg || null
    };
  }

  applyLivePreview() {
    applyThemeVars(this.getThemeFromState());
  }

  handleChange(field) {
    return (event) => {
      this.setState(
        {
          [field]: event.target.value,
          successMessage: null,
          errorMessage: null
        },
        this.applyLivePreview
      );
    };
  }

  handleLogoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 204800;
    if (file.size > MAX_SIZE) {
      this.setState({
        errorMessage: `Logo too large (${Math.round(file.size / 1024)}KB). Max 200KB.`,
        successMessage: null
      });
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.setState({ logo: e.target.result, successMessage: null, errorMessage: null }, this.applyLivePreview);
    };
    reader.onerror = () => {
      this.setState({ errorMessage: "Failed to read logo file.", successMessage: null });
    };
    reader.readAsDataURL(file);
  }

  handleSave() {
    try {
      const theme = saveTheme(this.getThemeFromState());
      this.savedTheme = theme;
      this.setState({
        successMessage: "Theme saved!",
        errorMessage: null
      });
      setTimeout(() => this.setState({ successMessage: null }), 3000);
    } catch (e) {
      this.setState({ errorMessage: `Save failed: ${e.message}`, successMessage: null });
    }
  }

  handleReset() {
    try {
      const theme = resetTheme();
      this.savedTheme = theme;
      this.setState({
        primaryColor: DEFAULT_THEME.primaryColor,
        secondaryColor: DEFAULT_THEME.secondaryColor,
        bgStart: DEFAULT_THEME.bgStart,
        bgEnd: DEFAULT_THEME.bgEnd,
        logo: null,
        fontFamily: "",
        surface: "",
        surfaceDark: "",
        surfaceMuted: "",
        text: "",
        textMuted: "",
        textInverse: "",
        success: "",
        error: "",
        borderColor: "",
        overlayBg: "",
        successMessage: "Reset to defaults!",
        errorMessage: null
      });
      setTimeout(() => this.setState({ successMessage: null }), 3000);
    } catch (e) {
      this.setState({ errorMessage: `Reset failed: ${e.message}`, successMessage: null });
    }
  }

  handleBack() {
    applyThemeVars(this.savedTheme);
    this.props.navigate("/");
  }

  handleExport() {
    const json = exportThemeJson(this.getThemeFromState());
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "theme.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  handleImportClick() {
    this.importFileRef.current?.click();
  }

  handleImportFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const theme = importThemeJson(e.target.result);
        this.setState(
          {
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor,
            bgStart: theme.bgStart,
            bgEnd: theme.bgEnd,
            logo: theme.logo,
            fontFamily: theme.fontFamily || "",
            surface: theme.surface || "",
            surfaceDark: theme.surfaceDark || "",
            surfaceMuted: theme.surfaceMuted || "",
            text: theme.text || "",
            textMuted: theme.textMuted || "",
            textInverse: theme.textInverse || "",
            success: theme.success || "",
            error: theme.error || "",
            borderColor: theme.borderColor || "",
            overlayBg: theme.overlayBg || "",
            successMessage: "Theme imported! Click Save to keep changes.",
            errorMessage: null
          },
          this.applyLivePreview
        );
      } catch (err) {
        this.setState({ errorMessage: `Import failed: ${err.message}`, successMessage: null });
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  renderColorInput(label, field, placeholder) {
    const value = this.state[field];
    return (
      <Form.Group as={Row} className="mb-3" key={field}>
        <Form.Label column sm={4}>{label}</Form.Label>
        <Col sm={8}>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Form.Control
              type="color"
              value={value || placeholder || "#000000"}
              onChange={this.handleChange(field)}
              style={{ width: "60px", height: "38px", padding: "2px" }}
            />
            <Form.Control
              type="text"
              value={value}
              onChange={this.handleChange(field)}
              placeholder={placeholder}
              style={{ flex: 1 }}
            />
          </div>
        </Col>
      </Form.Group>
    );
  }

  renderPreview() {
    const s = this.state;
    const bgStart = s.bgStart || DEFAULT_THEME.bgStart;
    const bgEnd = s.bgEnd || DEFAULT_THEME.bgEnd;
    const surface = s.surface || CSS_DEFAULTS.surface;
    const surfaceDark = s.surfaceDark || CSS_DEFAULTS.surfaceDark;
    const text = s.text || CSS_DEFAULTS.text;
    const textInverse = s.textInverse || CSS_DEFAULTS.textInverse;
    const success = s.success || CSS_DEFAULTS.success;
    const error = s.error || CSS_DEFAULTS.error;
    const primary = s.primaryColor;

    return (
      <div style={{ marginTop: "1.5rem" }}>
        <h6 style={{ color: "var(--text-inverse)", marginBottom: "1rem" }}>Live Preview</h6>

        {/* Gradient background swatch */}
        <div style={{ marginBottom: "1rem" }}>
          <small style={{ color: "var(--text-inverse)", opacity: 0.8 }}>Background Gradient</small>
          <div
            style={{
              height: "60px",
              borderRadius: "var(--radius-sm)",
              background: `linear-gradient(45deg, ${bgStart} 13%, ${bgEnd} 100%)`,
              border: "1px solid rgba(255,255,255,0.2)"
            }}
          />
        </div>

        {/* Question card preview */}
        <div style={{ marginBottom: "1rem" }}>
          <small style={{ color: "var(--text-inverse)", opacity: 0.8 }}>Question Card</small>
          <div
            style={{
              backgroundColor: surface,
              color: text,
              borderRadius: "var(--radius-sm)",
              padding: "1rem",
              fontSize: "0.9rem"
            }}
          >
            <strong>What is the capital of France?</strong>
            <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <span
                style={{
                  backgroundColor: surfaceDark,
                  color: textInverse,
                  padding: "0.25rem 0.75rem",
                  borderRadius: "4px",
                  fontSize: "0.8rem"
                }}
              >
                A
              </span>
              <span style={{ color: text }}>Paris</span>
            </div>
          </div>
        </div>

        {/* Button preview */}
        <div style={{ marginBottom: "1rem" }}>
          <small style={{ color: "var(--text-inverse)", opacity: 0.8 }}>Buttons</small>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
            <button
              style={{
                backgroundColor: primary,
                color: text,
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "var(--radius-sm)",
                fontWeight: 500
              }}
            >
              Primary
            </button>
            <button
              style={{
                backgroundColor: surfaceDark,
                color: textInverse,
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "var(--radius-sm)",
                fontWeight: 500
              }}
            >
              Secondary
            </button>
          </div>
        </div>

        {/* Alert preview */}
        <div>
          <small style={{ color: "var(--text-inverse)", opacity: 0.8 }}>Alerts</small>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
            <div
              style={{
                backgroundColor: success,
                color: text,
                padding: "0.4rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.8rem",
                flex: 1,
                textAlign: "center"
              }}
            >
              Success
            </div>
            <div
              style={{
                backgroundColor: error,
                color: "#fff",
                padding: "0.4rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.8rem",
                flex: 1,
                textAlign: "center"
              }}
            >
              Error
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <CenterBox>
        <Container fluid style={{ maxWidth: "900px" }}>
          <Row className="mb-4">
            <Col>
              <h2 style={{ color: "var(--text-inverse)", textAlign: "center", marginBottom: "1.5rem" }}>
                Theme Editor
              </h2>
            </Col>
          </Row>

          {this.state.successMessage && (
            <Alert variant="success" dismissible onClose={() => this.setState({ successMessage: null })}>
              {this.state.successMessage}
            </Alert>
          )}

          {this.state.errorMessage && (
            <Alert variant="danger" dismissible onClose={() => this.setState({ errorMessage: null })}>
              {this.state.errorMessage}
            </Alert>
          )}

          <Row>
            {/* Left column: Form */}
            <Col lg={7}>
              <Card style={{ borderRadius: "var(--radius)", boxShadow: "var(--shadow)", backgroundColor: "var(--surface-translucent)" }}>
                <Card.Body style={{ padding: "1.5rem" }}>
                  <Form>
                    {/* Logo */}
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm={4}>Logo</Form.Label>
                      <Col sm={8}>
                        {this.state.logo && (
                          <img
                            src={this.state.logo}
                            alt="Logo"
                            style={{
                              maxWidth: "200px",
                              maxHeight: "80px",
                              objectFit: "contain",
                              borderRadius: "var(--radius-sm)",
                              border: "1px solid var(--border-muted)",
                              padding: "0.25rem",
                              backgroundColor: "var(--surface)",
                              marginBottom: "0.5rem",
                              display: "block"
                            }}
                          />
                        )}
                        <Form.Control type="file" accept="image/png,image/svg+xml" onChange={this.handleLogoChange} />
                        <Form.Text className="text-muted">PNG/SVG, max 200KB</Form.Text>
                      </Col>
                    </Form.Group>

                    <hr />

                    {/* Basic colors */}
                    <h6 className="mb-3">Colors</h6>
                    {this.renderColorInput("Primary", "primaryColor", "#ffb300")}
                    {this.renderColorInput("Secondary", "secondaryColor", "#111827")}
                    {this.renderColorInput("Gradient Start", "bgStart", "#093879")}
                    {this.renderColorInput("Gradient End", "bgEnd", "#00d4ff")}

                    <hr />

                    {/* Font */}
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm={4}>Font Family</Form.Label>
                      <Col sm={8}>
                        <Form.Control
                          type="text"
                          value={this.state.fontFamily}
                          onChange={this.handleChange("fontFamily")}
                          placeholder="system-ui, Roboto, sans-serif"
                        />
                      </Col>
                    </Form.Group>

                    {/* Advanced section */}
                    <Accordion className="mb-3">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>Advanced Options</Accordion.Header>
                        <Accordion.Body>
                          <h6 className="mb-3 mt-2">Surfaces</h6>
                          {this.renderColorInput("Surface", "surface", "#ffffff")}
                          {this.renderColorInput("Surface Dark", "surfaceDark", "#343a40")}
                          {this.renderColorInput("Surface Muted", "surfaceMuted", "#6c757d")}

                          <h6 className="mb-3 mt-4">Text</h6>
                          {this.renderColorInput("Text", "text", "#111827")}
                          {this.renderColorInput("Text Muted", "textMuted", "#6c757d")}
                          {this.renderColorInput("Text Inverse", "textInverse", "#ffffff")}

                          <h6 className="mb-3 mt-4">Semantic</h6>
                          {this.renderColorInput("Success", "success", "#7bd447")}
                          {this.renderColorInput("Error", "error", "#dc3545")}

                          <h6 className="mb-3 mt-4">Other</h6>
                          {this.renderColorInput("Border Color", "borderColor", "rgba(0,0,0,0.15)")}
                          {this.renderColorInput("Overlay BG", "overlayBg", "rgba(0,0,0,0.75)")}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>

                    <hr />

                    {/* Action buttons */}
                    <div className="d-flex justify-content-between flex-wrap gap-2">
                      <div className="d-flex gap-2">
                        <IconButton icon={ArrowBack} label="Back" variant="secondary" onClick={this.handleBack} />
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button variant="outline-secondary" size="sm" onClick={this.handleImportClick}>
                          <img src={Publish} alt="" style={{ width: 18, height: 18, marginRight: 4, filter: "invert(0.5)" }} />
                          Import
                        </Button>
                        <input
                          type="file"
                          ref={this.importFileRef}
                          accept="application/json"
                          onChange={this.handleImportFile}
                          style={{ display: "none" }}
                        />
                        <Button variant="outline-secondary" size="sm" onClick={this.handleExport}>
                          <img src={GetApp} alt="" style={{ width: 18, height: 18, marginRight: 4, filter: "invert(0.5)" }} />
                          Export
                        </Button>
                        <IconButton icon={Close} label="Reset" variant="outline-secondary" onClick={this.handleReset} />
                        <IconButton icon={Check} label="Save" variant="warning" onClick={this.handleSave} />
                      </div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Right column: Preview */}
            <Col lg={5} className="mt-4 mt-lg-0">
              <Card style={{ borderRadius: "var(--radius)", backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <Card.Body style={{ padding: "1.5rem" }}>
                  {this.renderPreview()}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </CenterBox>
    );
  }
}

const ThemeEditorWithNavigate = (props) => {
  const navigate = useNavigate();
  return <ThemeEditor {...props} navigate={navigate} />;
};

export default ThemeEditorWithNavigate;
